import { getAuthToken, isAccessTokenExpired, setPendingLogoutReason } from '../authSession';
import { getStoredLocale } from '../locale';
import { ApiError, AuthRedirectError, RefreshTransientError, RefreshUnauthorizedError, TimeoutError } from './error';
import { runWithRefreshLeadership } from './refreshLock';

export type PaginationMeta = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type PagedData<T> = {
  data: T[];
  meta: PaginationMeta;
};

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001';

export const getApiUrl = () => apiUrl;

const shouldAttachAgentTeamsLocaleHeader = (input: RequestInfo | URL): boolean => {
  const baseUrl = getApiUrl();

  if (typeof input === 'string') {
    return input.startsWith(baseUrl);
  }

  if (input instanceof URL) {
    return input.toString().startsWith(baseUrl);
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.url.startsWith(baseUrl);
  }

  return false;
};

const withAgentTeamsLocaleHeader = (input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined => {
  if (!shouldAttachAgentTeamsLocaleHeader(input)) {
    return init;
  }

  const headers = new Headers(init?.headers);
  if (!headers.has('x-agentteams-locale')) {
    headers.set('X-AgentTeams-Locale', getStoredLocale());
  }
  if (!headers.has('x-agentteams-client')) {
    headers.set('X-AgentTeams-Client', 'web');
  }

  return {
    ...init,
    headers,
  };
};

export const authHeaders = (token: string, includeJson = false) => ({
  ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${token}`,
});

const FETCH_TIMEOUT_MS = 30000;

export const fetchWithTimeout = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const callerSignal = init?.signal;
  let isTimeout = false;
  const timeoutId = setTimeout(() => {
    isTimeout = true;
    controller.abort();
  }, FETCH_TIMEOUT_MS);
  const abortFromCaller = () => {
    controller.abort(callerSignal?.reason);
  };

  try {
    if (callerSignal?.aborted) {
      abortFromCaller();
    } else {
      callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
    }

    const requestInit = withAgentTeamsLocaleHeader(input, init);
    const response = await fetch(input, {
      ...(requestInit ?? {}),
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (controller.signal.aborted && isTimeout) {
      throw new TimeoutError();
    }
    // Non-timeout abort (e.g. unmount) — callers check error.name === 'AbortError' to suppress toast
    throw error;
  } finally {
    callerSignal?.removeEventListener('abort', abortFromCaller);
    clearTimeout(timeoutId);
  }
};

type ParseApiErrorOptions = {
  skipRedirectOn401?: boolean;
};

export type RefreshOutcome =
  | { status: 'refreshed'; token: string }
  | { status: 'unauthorized' }
  | { status: 'transient' }
  | { status: 'no-session' };

// Responses whose 401 must NOT trigger a forced logout from parseApiError
// (set when refresh failed due to a transient/network/5xx error).
const suppressRedirectResponses = new WeakSet<Response>();

// --- Token Refresh Queue ---
let refreshPromise: Promise<RefreshOutcome> | null = null;

export const tryRefreshToken = async (): Promise<RefreshOutcome> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      const { getAuthSession, saveAuthSession } = await import('../authSession');
      const initialSession = getAuthSession();
      if (!initialSession?.refreshToken) {
        return { status: 'no-session' };
      }
      const initialToken = initialSession.token;

      return await runWithRefreshLeadership(async (): Promise<RefreshOutcome> => {
        // 락 안에서 세션 재조회: 다른 탭이 이미 refresh를 끝냈다면 API 호출을 건너뛴다.
        const currentSession = getAuthSession();
        if (!currentSession?.refreshToken) {
          return { status: 'no-session' };
        }
        if (currentSession.token !== initialToken) {
          return { status: 'refreshed', token: currentSession.token };
        }

        const { refreshAccessToken } = await import('./auth');
        try {
          const refreshed = await refreshAccessToken(currentSession.refreshToken);
          saveAuthSession({
            ...currentSession,
            token: refreshed.token,
            refreshToken: refreshed.refreshToken ?? currentSession.refreshToken,
          });
          return { status: 'refreshed', token: refreshed.token };
        } catch (error) {
          if (error instanceof RefreshUnauthorizedError) {
            // 서버가 확인한 종료 사유(세션 제한)를 보존해 이후 handle401Response가 로그인 안내에 사용한다.
            setPendingLogoutReason(error.reason);
            return { status: 'unauthorized' };
          }
          if (error instanceof RefreshTransientError) {
            return { status: 'transient' };
          }
          // Unknown errors are treated as transient to avoid surprise logouts.
          return { status: 'transient' };
        }
      });
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const ensureFreshAccessToken = async (skewMs = 30_000): Promise<void> => {
  const token = getAuthToken();
  if (!token) return;
  if (!isAccessTokenExpired(token, skewMs)) return;
  // outcome 무관: unauthorized/no-session/transient 케이스는 기존 401 fallback path가 처리한다.
  await tryRefreshToken();
};

const applyFreshAuthHeader = (init?: RequestInit): RequestInit | undefined => {
  if (!init) return init;
  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) return init;
  const token = getAuthToken();
  if (!token) return init;
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

export const fetchWithAuthRetry = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  // 무조건 await하면 토큰이 신선해도 microtask가 끼어 호출자 동기 흐름이 깨지므로,
  // 만료된 케이스에서만 await한다.
  const token = getAuthToken();
  if (token && isAccessTokenExpired(token, 30_000)) {
    await tryRefreshToken();
  }
  const initWithFreshAuth = applyFreshAuthHeader(init);
  const response = await fetchWithTimeout(input, initWithFreshAuth);

  if (response.status !== 401) return response;

  const outcome = await tryRefreshToken();

  if (outcome.status === 'refreshed') {
    const retryHeaders = new Headers(init?.headers);
    retryHeaders.set('Authorization', `Bearer ${outcome.token}`);
    const retryResponse = await fetchWithTimeout(input, { ...init, headers: retryHeaders });
    if (retryResponse.status === 401) {
      // refresh가 방금 성공했으므로 세션(refresh token)은 유효하다. 이 401은 서버측
      // 일시 장애/오분류로 보고 강제 로그아웃을 막는다. 진짜 무효 세션은 다음
      // refresh 시도가 unauthorized로 걸러내 기존 로그아웃 경로를 탄다.
      suppressRedirectResponses.add(retryResponse);
    }
    return retryResponse;
  }

  if (outcome.status === 'transient') {
    // Let the original request fail without triggering a forced logout.
    suppressRedirectResponses.add(response);
  }

  return response;
};

export const readServerRequestId = (response: Response): string | undefined => {
  try {
    const requestId = response.headers?.get?.('x-request-id');
    return typeof requestId === 'string' && requestId.length > 0 ? requestId : undefined;
  } catch {
    return undefined;
  }
};

export const parseApiError = async (
  response: Response,
  fallback: string,
  options?: ParseApiErrorOptions,
): Promise<ApiError | AuthRedirectError> => {
  const shouldRedirectOn401 =
    response.status === 401 && !options?.skipRedirectOn401 && !suppressRedirectResponses.has(response);

  if (shouldRedirectOn401) {
    const { handle401Response } = await import('../authSession');
    handle401Response();
    return new AuthRedirectError();
  }

  let errorCode: string | undefined = undefined;
  let errorDetailCode: string | undefined = undefined;
  let message: string | undefined = undefined;
  let cycleTaskNumbers: number[] | undefined = undefined;
  let blockedTaskId: string | undefined = undefined;
  let blockedTaskNumber: number | undefined = undefined;
  let blockedTaskTitle: string | undefined = undefined;
  let blockedEngine: string | undefined = undefined;
  let blockedModel: string | undefined = undefined;

  try {
    const body = (await response.json()) as unknown;
    if (body && typeof body === 'object') {
      if ('errorCode' in body && typeof (body as { errorCode?: unknown }).errorCode === 'string') {
        errorCode = (body as { errorCode: string }).errorCode;
      }
      if ('errorDetailCode' in body && typeof (body as { errorDetailCode?: unknown }).errorDetailCode === 'string') {
        errorDetailCode = (body as { errorDetailCode: string }).errorDetailCode;
      }
      if ('message' in body && typeof (body as { message?: unknown }).message === 'string') {
        message = (body as { message: string }).message;
      }
      if (
        'cycleTaskNumbers' in body &&
        Array.isArray((body as { cycleTaskNumbers?: unknown }).cycleTaskNumbers) &&
        (body as { cycleTaskNumbers: unknown[] }).cycleTaskNumbers.every((item) => typeof item === 'number')
      ) {
        cycleTaskNumbers = (body as { cycleTaskNumbers: number[] }).cycleTaskNumbers;
      }
      if ('blockedTaskId' in body && typeof (body as { blockedTaskId?: unknown }).blockedTaskId === 'string') {
        blockedTaskId = (body as { blockedTaskId: string }).blockedTaskId;
      }
      if (
        'blockedTaskNumber' in body &&
        typeof (body as { blockedTaskNumber?: unknown }).blockedTaskNumber === 'number'
      ) {
        blockedTaskNumber = (body as { blockedTaskNumber: number }).blockedTaskNumber;
      }
      if ('blockedTaskTitle' in body && typeof (body as { blockedTaskTitle?: unknown }).blockedTaskTitle === 'string') {
        blockedTaskTitle = (body as { blockedTaskTitle: string }).blockedTaskTitle;
      }
      if ('blockedEngine' in body && typeof (body as { blockedEngine?: unknown }).blockedEngine === 'string') {
        blockedEngine = (body as { blockedEngine: string }).blockedEngine;
      }
      if ('blockedModel' in body && typeof (body as { blockedModel?: unknown }).blockedModel === 'string') {
        blockedModel = (body as { blockedModel: string }).blockedModel;
      }
    }
  } catch {}

  return new ApiError(
    {
      statusCode: response.status,
      errorCode,
      errorDetailCode,
      message,
      cycleTaskNumbers,
      blockedTaskId,
      blockedTaskNumber,
      blockedTaskTitle,
      blockedEngine,
      blockedModel,
      requestId: readServerRequestId(response),
    },
    fallback,
  );
};

export const parseErrorMessage = async (
  response: Response,
  fallback: string,
  options?: { skipRedirectOn401?: boolean },
) => {
  const error = await parseApiError(response, fallback, options);
  return error instanceof AuthRedirectError ? '' : error.message;
};
